import * as m from '@/paraglide/messages';
import { Schema } from 'effect';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { effectQueryErrorMessage } from '@listening/frontend/query/query-error';
import type { NoteRow as Note } from '@listening/frontend/query/notes';
import { Button } from '@listening/frontend/components/radix/button';
import { DialogContent } from '@listening/frontend/components/radix/dialog';
import { DialogCloseContext } from '@listening/frontend/components/custom/dialog-dropdown';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@listening/frontend/components/radix/form';
import { Input } from '@listening/frontend/components/radix/input';
import { Textarea } from '@listening/frontend/components/radix/textarea';
import { formatRelative } from 'date-fns';
import { use, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { CoverImgWithBackgroundOld } from '@/components/shared-ui/audio/cover-img-with-background';
import { audioService } from '@/services/audio-service';
import { useUpsertNoteMutation } from '@listening/frontend/query/notes-collection';

const formSchema = Schema.Struct({
  title: Schema.String,
  body: Schema.String,
});
type FormValues = typeof formSchema.Type;

export function EditNoteDialog({
  noteItem: note,
  closeDialogFn,
}: {
  noteItem: Note;
  closeDialogFn?: () => void;
}) {
  const closeContext = use(DialogCloseContext);
  const [now] = useState(() => new Date());
  const closeDialog = closeDialogFn ?? closeContext.closeDialog;

  const audioItem = audioService.store.useCurrentAudio().query.data;

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(
      Schema.toStandardSchemaV1(formSchema),
    ) as Resolver<FormValues>,
    defaultValues: {
      title: note.title,
      body: note.body,
    },
  });

  const upsertNoteMutation = useUpsertNoteMutation();

  return (
    <DialogContent className='flex max-h-[90%] w-2/3 flex-col'>
      <Form {...form}>
        <form
          className='flex w-full flex-col items-center gap-x-6 gap-y-2 p-2'
          onSubmit={form.handleSubmit((values: FormValues) => {
            upsertNoteMutation.mutate([{ ...note, ...values }], {
              onSuccess: () => {
                closeDialog();
              },
              onError: (e) =>
                toast.error(
                  effectQueryErrorMessage(e) ??
                    'There was an error while saving the note',
                ),
              onSettled: () => {
                upsertNoteMutation.reset();
              },
            });
          })}
        >
          <div className='flex gap-4'>
            <CoverImgWithBackgroundOld
              className='row-span-2 size-12 justify-center rounded-xl px-1 pt-1'
              imgClassName='mt-0 w-full rounded-sm '
              imgSrc={audioItem?.cover_img_url}
            />
            <div className='flex flex-col'>
              <p>
                {m.dialog_note_created()}{' '}
                {formatRelative(
                  note.created_at_ms ? new Date(note.created_at_ms) : now,
                  now,
                )}
              </p>
              <p>
                {m.dialog_note_updated()}{' '}
                {formatRelative(
                  note.updated_at_ms ? new Date(note.updated_at_ms) : now,
                  now,
                )}
              </p>
            </div>
          </div>
          <FormField
            control={form.control}
            name='title'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel>{m.dialog_note_title()}</FormLabel>
                <FormControl>
                  <Input {...field} className='w-full' type='text' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='body'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel>{m.dialog_note_body()}</FormLabel>
                <FormControl>
                  <Textarea {...field} className='h-48 w-full' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button className='row-span-2 mt-4 w-40 rounded-full' type='submit'>
            {m.common_save()}
          </Button>
        </form>
      </Form>
    </DialogContent>
  );
}
